import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { AppError } from 'app-error';
import { AppLoader } from 'app-loader';
import { MainBackground } from 'components/background/main-background/main-background';
import { MainBackgroundProvider } from 'components/background/main-background/main-background-context';
import TopMenu from 'components/menu/top-menu/top-menu';
import { usePlatform } from 'core/hooks/platform';
import { DataLayerStore } from 'core/stores/data-layer.store';
import { LiveChatWrapper } from 'live-chat/live-chat-wrapper';
import PaymentContextProvider from 'payment/payment.context';
import WelcomeScreen from 'welcome-screen/welcome-screen';
import { AppStore } from './app.store';
import { SideMenu } from './components/menu/side-menu/side-menu';
import { FeaturePanel } from './components/panels/feature-panel/feature-panel';
import { Panel } from './components/panels/panel';
import { PanelType } from './components/panels/panel-type';
import { AppContextProvider } from './core/context/app.context';
import { FeatureContextProvider } from './core/context/feature.context';
import { UtmTypes } from './core/enums/analytics-journey-key';
import { Router } from './core/router/router';
import ScrollToTop from './core/router/scroll-to-top';
import { AnalyticsStructureStore } from './core/stores/analytics-structure.store';
import { FeatureStore } from './core/stores/feature.store';
import Maintenance from './landing/maintenance/maintenance';
import styles from './app.module.scss';

const App = observer<{ isLoading: boolean }>(() => {
  const appStore = useLocalObservable(() => new AppStore());
  const featureStore = useLocalObservable(() => new FeatureStore());
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { translate } = usePlatform();
  const dataLayerStore = DataLayerStore.GetInstance;

  // Main app initialization
  useEffect(() => {
    appStore.initialize(featureStore);
    appStore.changeTitle(translate('title.main'));

    // initializes base data layer
    dataLayerStore.initialize(appStore.country, appStore.lang).set();
  }, [appStore, featureStore]);

  // Location/Path tracking
  useEffect(() => {
    appStore.changePathname(location.pathname);
    AnalyticsStructureStore.updateProperty('href', window.location.href);
  }, [appStore, location.pathname]);

  // Set default data-layer
  useEffect(() => {
    const search = window.location.search;
    const searchParams = new URLSearchParams(search);
    const hasAcquisitionTags = Object.values(UtmTypes).some((type) => searchParams.has(type));

    if (!hasAcquisitionTags) {
      // Pulls from storage if available
      dataLayerStore.setAcquisitionData();
      return;
    }

    const acquisitionData = dataLayerStore.getAcquisitionTags(searchParams);

    dataLayerStore.setAcquisitionData(acquisitionData);

    window.location.replace(window.location.href.replace(search, ''));
  }, [window.location.search]);

  // TODO: Move to live chat component
  useEffect(() => {
    const openLiveChat = searchParams.has('openLiveChat');
    if (openLiveChat) {
      appStore.changeOpenLiveChat(true);
    }
  }, [searchParams]);

  const containerPanelClasses = () => {
    const topPanel = !appStore.alternativeLayout ? 'top-page-panel' : '';
    const customImageBackground = !appStore.isLoading && appStore.background ? 'custom-image-background' : '';
    const menuHeight = appStore.dynamicMenuHeight ? '' : `${topPanel} overflow-hidden`;

    return [customImageBackground, menuHeight, styles.mainPanel];
  };

  const mainContentClasses = () => {
    const contentClass = !appStore.alternativeLayout ? styles.mainContent : styles.alternateContent;
    const overlayClass = appStore.activeRoute?.isOverlay ? styles.mainContentOverlay : '';

    return [contentClass, overlayClass];
  };

  return (
    <Panel classes={containerPanelClasses()} sizeType={PanelType.FullWidth} productBackground={!!appStore.background}>
      <MainBackgroundProvider>
        <MainBackground />

        <Helmet>
          <title>{appStore.title}</title>
          <body
            id="residential-self-service"
            className={`residential-self-service ${
              document.body?.classList.contains('is-modal-open') ? 'is-modal-open' : ''
            }`}
            data-theme={appStore.intendedPlatform}
            data-site={appStore.intendedPlatform}
          ></body>
        </Helmet>

        {appStore.showTopMenu && (
          <TopMenu
            userConfig={appStore.menuConfig}
            location={location}
            isAuthorized={appStore.isAuthorized}
            inbox={appStore.inboxDisplay}
            changeDynamicMenuHeight={(value: boolean) => appStore.changeDynamicMenuHeight(value)}
          />
        )}

        <AppError>
          <AppContextProvider appStore={appStore}>
            <PaymentContextProvider paymentStore={appStore.paymentStore}>
              <AppLoader>
                <FeatureContextProvider featureStore={featureStore}>
                  <FeaturePanel hasFeature={featureStore.inMaintenanceMode !== false}>
                    <Maintenance panelType={featureStore.inMaintenanceMode}></Maintenance>
                  </FeaturePanel>

                  <FeaturePanel hasFeature={appStore.showLiveChat}>
                    <LiveChatWrapper shouldOpen={appStore.openLiveChat} />
                  </FeaturePanel>

                  <FeaturePanel hasFeature={appStore.showWelcomePopup && !appStore.showBrandMissMatchPopup}>
                    <WelcomeScreen />
                  </FeaturePanel>

                  <FeaturePanel hasFeature={appStore.showBrandMissMatchPopup}>
                    <WelcomeScreen template="brandMissMatch" />
                  </FeaturePanel>

                  <FeaturePanel hasFeature={!featureStore.inMaintenanceMode && !appStore.showBrandMissMatchPopup}>
                    <div className={!appStore.alternativeLayout ? 'app-container' : styles.alternateAppContainer}>
                      {appStore.showSideMenu && (
                        <SideMenu
                          isAuthenticated={appStore.isAuthenticated}
                          routes={appStore.routes?.menuItems}
                          activeRoute={appStore.activeRoute}
                          onChangeActiveMenuItem={(route: string | null) => appStore.changeActiveRoute(route)}
                        ></SideMenu>
                      )}

                      <div className={clsx(mainContentClasses())}>
                        <main className={styles.main}>
                          <ScrollToTop />

                          <Router
                            routes={appStore.routes?.availableRoutes}
                            onLoad={(route) => appStore.changeActiveRoute(route.name)}
                          ></Router>
                        </main>
                      </div>
                    </div>
                  </FeaturePanel>
                </FeatureContextProvider>
              </AppLoader>
            </PaymentContextProvider>
          </AppContextProvider>
        </AppError>
      </MainBackgroundProvider>
    </Panel>
  );
});

export default App;
